import { formatStatus } from '@/lib/utils'
import type { components } from '@/api/schema'

export type PurchaseOrderAction = components['schemas']['PurchaseOrderActionRead']
export type PurchaseOrderActionReopen = components['schemas']['PurchaseOrderActionReopenRead']

export type ActorName = (value: string | null | undefined) => string | null

/** What every action item shares, PO or quote (USA-1094), for the email-thread views. */
export type ActionItemLike = Pick<
  PurchaseOrderAction,
  | 'id'
  | 'action_type'
  | 'status'
  | 'summary'
  | 'created_by'
  | 'created_at'
  | 'resolved_by'
  | 'resolved_at'
  | 'resolution_data'
  | 'communication_message_id'
> & {
  card?: PurchaseOrderAction['card']
  progress?: PurchaseOrderAction['progress']
  resolution_reason?: PurchaseOrderAction['resolution_reason']
}

export type ActionAuditEvent<A extends ActionItemLike = PurchaseOrderAction> = {
  key: string
  action: A
  kind: 'raised' | 'resolved' | 'dismissed' | 'reopened' | 'started' | 'stopped'
  actor: string | null
  at: string
  /**
   * Under a close: why automation closed it (USA-1674), or the staff note left on an earlier close
   * before the action was reopened.
   */
  note?: string | null
}

export function actionTypeBadge(actionType: string) {
  const colors: Record<string, string> = {
    order_cancellation: 'bg-red-100 text-red-800',
    operational_hold: 'bg-amber-100 text-amber-800',
    delay: 'bg-yellow-100 text-yellow-800',
    customer_delay_notice: 'bg-yellow-100 text-yellow-800',
    ship_date_passed: 'bg-yellow-100 text-yellow-800',
    backorder: 'bg-orange-100 text-orange-800',
    substitution: 'bg-blue-100 text-blue-800',
    discontinued: 'bg-red-100 text-red-800',
    additional_charge: 'bg-orange-100 text-orange-800',
    payment_charge: 'bg-purple-100 text-purple-800',
    info_request: 'bg-purple-100 text-purple-800',
    account_setup: 'bg-purple-100 text-purple-800',
    price_change: 'bg-orange-100 text-orange-800',
    discount_changed: 'bg-orange-100 text-orange-800',
    freight_policy_change: 'bg-yellow-100 text-yellow-800',
    freight_mode: 'bg-yellow-100 text-yellow-800',
    source_change: 'bg-orange-100 text-orange-800',
    // An agent was stopped from a change it had to make (USA-1697).
    unable_to_act: 'bg-red-100 text-red-800',
    other: 'bg-gray-100 text-gray-800',
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors[actionType] || 'bg-gray-100 text-gray-800'}`}
    >
      {formatStatus(actionType)}
    </span>
  )
}

/**
 * Raised event, then in time order each earlier resolution and its reopening and each time someone
 * started or stopped working it (USA-1443), then the resolved/dismissed event once the action is closed.
 */
export function actionAuditEvents<A extends ActionItemLike>(
  action: A,
  actorName: ActorName,
  reopens: readonly PurchaseOrderActionReopen[] = [],
): ActionAuditEvent<A>[] {
  const events: ActionAuditEvent<A>[] = [
    {
      key: `${action.id}-raised`,
      action,
      kind: 'raised',
      actor: actorName(action.created_by),
      at: action.created_at,
    },
  ]
  const actionReopens = reopens
    .filter((reopen) => reopen.action_id === action.id)
    .sort((a, b) => a.reopened_at.localeCompare(b.reopened_at))
  for (const reopen of actionReopens) {
    if (reopen.previous_resolved_at) {
      events.push({
        key: `${action.id}-closed-${reopen.id}`,
        action,
        kind: reopen.previous_status === 'dismissed' ? 'dismissed' : 'resolved',
        actor: actorName(reopen.previous_resolved_by),
        at: reopen.previous_resolved_at,
        note: notesFrom(reopen.previous_resolution_data) ?? reopen.previous_resolution_reason ?? null,
      })
    }
    events.push({
      key: `${action.id}-reopened-${reopen.id}`,
      action,
      kind: 'reopened',
      actor: actorName(reopen.reopened_by),
      at: reopen.reopened_at,
    })
  }
  for (const stretch of action.progress ?? []) {
    events.push({
      key: `${action.id}-started-${stretch.id}`,
      action,
      kind: 'started',
      actor: actorName(stretch.started_by),
      at: stretch.started_at,
    })
    // A stretch closed by resolving or reopening the item is told by that event instead.
    const endedByClosure =
      stretch.ended_at != null &&
      (stretch.ended_at === action.resolved_at ||
        actionReopens.some((reopen) => reopen.reopened_at === stretch.ended_at))
    if (stretch.ended_at && !endedByClosure) {
      events.push({
        key: `${action.id}-stopped-${stretch.id}`,
        action,
        kind: 'stopped',
        actor: actorName(stretch.ended_by),
        at: stretch.ended_at,
      })
    }
  }
  events.sort((a, b) => (a.kind === 'raised' ? -1 : b.kind === 'raised' ? 1 : a.at.localeCompare(b.at)))
  if (action.status !== 'open' && action.resolved_at) {
    events.push({
      key: `${action.id}-closed`,
      action,
      kind: action.status === 'dismissed' ? 'dismissed' : 'resolved',
      actor: actorName(action.resolved_by),
      at: action.resolved_at,
      note: action.resolution_reason ?? null,
    })
  }
  return events
}

function parseResolutionData(raw: string | null | undefined): Record<string, unknown> | null {
  if (!raw) return null
  try {
    const data: unknown = JSON.parse(raw)
    return typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : null
  } catch {
    return null
  }
}

function resolutionData(action: ActionItemLike): Record<string, unknown> | null {
  return parseResolutionData(action.resolution_data)
}

function notesFrom(raw: string | null | undefined): string | null {
  const notes = parseResolutionData(raw)?.notes
  return typeof notes === 'string' && notes.trim() ? notes.trim() : null
}

export function actionResolutionNotes(action: ActionItemLike): string | null {
  return notesFrom(action.resolution_data)
}

/** Staff notes, or why automation closed it (the AI's reason when it resolved it from an email). */
export function actionResolutionExplanation(action: ActionItemLike): string | null {
  const aiNote = resolutionData(action)?.resolution_note
  return (
    actionResolutionNotes(action) ??
    (typeof aiNote === 'string' && aiNote.trim() ? aiNote.trim() : null) ??
    action.resolution_reason ??
    null
  )
}

/** The email the AI read when it resolved this action, if it was resolved from an email. */
export function actionResolvingMessageId(action: ActionItemLike): number | null {
  const id = resolutionData(action)?.communication_message_id
  return typeof id === 'number' ? id : null
}
